// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper from "@adl/App/Components/Common/SectionWrapper";
import Notify from "@adl/App/Components/Notify";
import QuoteSummaryCard from "@adl/Overrides/Components/QuoteSummaryCard";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import acceptJobQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/AcceptJobQuote";
import translate from "@insite/client-framework/Translate";
import { QuoteModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        rfqJobQuotesPageUrl: getPageLinkByPageType(state, "RfqJobQuotesPage")?.url,
        cartState: getCurrentCartState(state),
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

// XNG-Changes
interface RecentQuotesState {
    cartNotificationModalIsOpen: boolean;
    selectedQuote: QuoteModel | undefined;
}

// XNG-Changes: add numberOfRecords
const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

type Props = OwnProps &
    HasHistory &
    HasToasterContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // XNG-Changes
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    dataTableActionHeaders?: DataTableHeaderProps;
    dataTableAmount?: DataTableHeaderProps;
    cartNotificationModal?: TwoButtonModalStyles;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
    dataTableActionHeaders: {
        css: css`
            min-width: 135px;
        `,
    },
    dataTableAmount: {
        css: css`
            min-width: 104px;
        `,
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    // XNG-Changes:
    state: RecentQuotesState = {
        cartNotificationModalIsOpen: false,
        selectedQuote: undefined,
    };
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            // XNG-Changes
            recentQuotesParameter.pageSize = this.props.fields.numberOfRecords || recentQuotesParameter.pageSize;
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const {
            settingsCollection,
            quotesDataView,
            myQuotesPageNavLink,
            canViewQuotes,
            history,
            cartState,
            session,
            rfqJobQuotesPageUrl,
            checkoutShippingPageUrl,
        } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const acceptQuoteClickHandler = (id: string) => {
            const quote = quotesDataView.value?.find(q => q.id === id);
            this.setState(prevState => ({ ...prevState, selectedQuote: quote }));
            if (quote && quote.isJobQuote) {
                acceptJobQuote({
                    quote,
                    onSuccess: () => {
                        this.props.toaster.addToast({ body: "Bid accepted", messageType: "success" });
                        redirectToJobQuotes();
                    },
                    onComplete(resultProps) {
                        if (resultProps.apiResult) {
                            // "this" is targeting the object being created, not the parent SFC
                            // eslint-disable-next-line react/no-this-in-sfc
                            this.onSuccess?.();
                        }
                    },
                });
            } else if (cartState.value?.lineCount === 0) {
                redirectToCheckout();
            } else {
                this.setState(prevState => ({ ...prevState, cartNotificationModalIsOpen: true }));
            }
        };

        const redirectToJobQuotes = () => {
            if (rfqJobQuotesPageUrl) {
                history.push(rfqJobQuotesPageUrl);
            }
        };

        const cancelCartNotificationHandler = () => {
            this.setState(prevState => ({ ...prevState, cartNotificationModalIsOpen: false }));
        };

        const submitCartNotificationHandler = () => {
            redirectToCheckout();
        };

        const redirectToCheckout = (quote?: QuoteModel) => {
            const quoteParameter = !quote && this.state.selectedQuote ? this.state.selectedQuote : quote;

            if (checkoutShippingPageUrl) {
                if (quoteParameter) {
                    history.push(`${checkoutShippingPageUrl}?cartId=${quoteParameter.id}`);
                }
            }
        };

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;
        // XNG-Changes:Remove Customer , remove Ship To , add Amount , actions
        return (
            <SectionWrapper
                isAccordion={true}
                title={translate("Recent Quotes")}
                link={myQuotesPageUrl}
                linkText={translate("View All")}
            >
                {quotesDataView.value.length === 0 && (
                    <Notify type="danger" {...styles.noQuotesText}>
                        {siteMessage("Rfq_NoQuotesMessage")}
                    </Notify>
                )}
                {quotesDataView.value.length > 0 && (
                    <>
                        <DataTable {...styles.dataTable} className="mobile-view">
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("َBid #")}</DataTableHeader>
                                {settingsCollection.quoteSettings.jobQuoteEnabled && (
                                    <>
                                        <DataTableHeader {...styles.dataTableHeaders}>
                                            {translate("َType")}
                                        </DataTableHeader>
                                    </>
                                )}
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Status")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("PO #")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Job Name")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Requested")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Expires")}</DataTableHeader>
                                {session.isSalesPerson && (
                                    <>
                                        <DataTableHeader {...styles.dataTableHeaders}>
                                            {translate("Sales Rep")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.dataTableHeaders}>
                                            {translate("User")}
                                        </DataTableHeader>
                                    </>
                                )}
                                {/* <DataTableHeader {...styles.dataTableHeaders}>{translate("Customer")}</DataTableHeader> */}
                                {/* {!session.isSalesPerson && (
                                <DataTableHeader {...styles.dataTableHeaders}>
                                    {translate("Ship To / Pick Up")}
                                </DataTableHeader>
                                 )} */}
                                <DataTableHeader tight {...styles.dataTableAmount}>
                                    {translate("Amount")}
                                </DataTableHeader>
                                {quotesDataView.value.some(({ status }) => status === "QuoteProposed") && (
                                    <DataTableHeader tight {...styles.dataTableActionHeaders}>
                                        {translate("Actions")}
                                    </DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {quotesDataView.value.map(
                                    quote =>
                                        quote !== null && (
                                            <DataTableRow
                                                {...styles.dataTableRow}
                                                data-test-selector="recentQuotes_dataLine"
                                                key={quote.id}
                                            >
                                                <QuoteSummaryCard
                                                    quote={quote}
                                                    showActions={
                                                        !!(
                                                            quotesDataView.value &&
                                                            quotesDataView.value.some(
                                                                ({ status }) => status === "QuoteProposed",
                                                            )
                                                        )
                                                    }
                                                    acceptQuoteClickHandler={acceptQuoteClickHandler}
                                                    session={session}
                                                    quoteSettings={settingsCollection.quoteSettings}
                                                    extendedStyles={styles.quoteSummaryCard}
                                                />
                                            </DataTableRow>
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                        <TwoButtonModal
                            {...styles.cartNotificationModal}
                            modalIsOpen={this.state.cartNotificationModalIsOpen}
                            headlineText={translate("Cart Notification")}
                            messageText={siteMessage("Rfq_CartNotificationMessage")}
                            cancelButtonText={translate("Cancel")}
                            submitButtonText={translate("Continue")}
                            onCancel={cancelCartNotificationHandler}
                            onSubmit={submitCartNotificationHandler}
                            submitTestSelector="submitCartNotification"
                        />
                    </>
                )}
            </SectionWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(RecentQuotes))),

    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
